import PickerPane from './PickerPane'
import PickerInput from './PickerInput'
import Popover from '../popover'
import { getComponentsByName } from '../../core/utils/KVdom'
import { PickerType, PickerTypes } from './constants'
import moment from 'moment-mini'
import _ from 'lodash'
import { Tag } from '../tag'
import { convertPropClass2ClassObjectLike } from '../../core/utils/KProps'

export default {
  name: 'KDatePicker',
  props: {
    // 传递的值是否是 moment 对象
    moment: {
      type: Boolean,
      default: false,
    },
    // 输入的时间是否合法
    value: {
      validator() {
        return true
      },
      // null
      default() {
        return null
      },
    },
    // 当 value 为空时，若存在 referenceValue，则会根据 referenceValue 展现对应的日期面板
    referenceValue: {
      validator() {
        return true
      },
      // null
      default() {
        return null
      },
    },
    // 输出值的格式
    valueFormat: {
      type: String,
      default: null,
    },
    // 类型，
    type: {
      validator: value => _.indexOf(PickerTypes, value) !== -1,
      default: 'date',
    },
    placeholder: {
      type: String,
      default: '请选择',
    },
    startPlaceholder: {
      type: String,
    },
    endPlaceholder: {
      type: String,
    },
    // input 显示格式
    format: {
      type: [String, Function],
      // null
      default() {
        return null
      },
    },
    startFormat: {
      type: [String, Function],
      // 'YYYY-MM-DD'
      default() {
        return null
      },
    },
    endFormat: {
      type: [String, Function],
      // 'YYYY-MM-DD'
      default() {
        return null
      },
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    rangeSeparator: {
      type: String,
      default: '-',
    },
    // 面板联动
    unlinkViews: {
      type: Boolean,
      default: false,
    },
    // 快捷选择
    shortcuts: {
      type: Array,
      // []
      default() {
        return []
      },
    },
    disabledDate: {
      type: Function,
    },
    blockPropagation: {
      type: Boolean,
    },
    hidePickerIcon: {
      type: Boolean,
      default: false,
    },
    // 一周的开始日期
    weekStartDay: {
      type: Number,
      default: 1,
    },
    // 是否开启多选
    multiple: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    // 多选条件下，日历面板数量
    paneCount: {
      type: Number,
      default: 1,
    },
    popperClass: {
      // 自定义 popper 类，将作用于 popper 上面
      validator: (value) => _.isString(value) || _.isArray(value) || _.isObjectLike(value),
      // {}
      default() {
        return {}
      },
    },
  },
  data() {
    return {
      innerValue: null,
      innerStart: null,
      innerEnd: null,
      pickingValue: [],
      now: null,
      active: false, // 下拉框是否展开
    }
  },
  computed: {
    hasValue() {
      if (this.type === PickerType.Date || this.type === PickerType.Time) {
        return _.isArray(this.innerValue) ? this.innerValue.length > 0 : !!this.innerValue
      } else if (this.type === PickerType.DateRange) {
        return this.innerValue && this.innerValue.length > 0
      }
    },
    popClass() {
      return _.assign(
        {
          'km-date-picker__popper': true,
        },
        convertPropClass2ClassObjectLike(this.popperClass),
      )
    },
  },
  watch: {
    value: {
      immediate: true,
      handler() {
        this.setInnerValue(this.valueFormat)
      },
    },
    active: {
      immediate: true,
      handler(value) {
        if (value) {
          // reset to make referenceValue in pickers refresh views
          // this.setInnerValue(this.valueFormat)
          this.now = moment()
        }
      },
    },
  },
  created() {
    this.handleOutputValue(this.innerValue)
  },
  mounted() {
    const popover = getComponentsByName(this.$children, 'KPopover')[0].popperVM
    this.$nextTick(() => {
      this.dropdown = getComponentsByName(popover.$children, 'KDatePickerPane')[0]
    })
  },
  methods: {
    // api method
    open() {
      setTimeout(() => {
        this.active = true
      }, 0)
    },
    setInnerValue(valueFormat) {
      if (this.type === PickerType.Date || this.type === PickerType.Time) {
        if (_.isArray(this.value)) {
          this.innerValue = this.value.length > 0 ? _.map(this.value, item => moment(item, valueFormat)) : []
        } else {
          this.innerValue = this.value ? moment(this.value, valueFormat) : null
        }
      } else if (this.type === PickerType.DateRange) {
        let value = this.value
          ? [this.value[0] ? moment(this.value[0], valueFormat) : undefined,
            this.value[1] ? moment(this.value[1], valueFormat) : undefined]
          : []
        if (value.length && value[0] === undefined && value[1] === undefined) {
          value = []
        }
        this.innerValue = value
      }
    },
    formatOutput(value) {
      if (!value) {
        return undefined
      }

      if (this.moment) {
        return value.clone()
      }

      if (this.valueFormat) {
        return value.clone().format(this.valueFormat)
      }

      return value.valueOf()
    },
    handleOutputValue(value) {
      if (this.type === PickerType.Time) {
        value = this.formatOutput(value)
      } else if (this.type === PickerType.Date) {
        value = _.isArray(value) ? _.map(value, item => this.formatOutput(item)) : this.formatOutput(value)
        // value = this.formatOutput(value) // value ? value.valueOf() : null
      } else if (this.type === PickerType.DateRange) {
        if (value.length) {
          value = [this.formatOutput(value[0]), this.formatOutput(value[1])]
        } else {
          value = []
        }
      }

      this.$emit('input', value)
    },
    handleValueChange(value) {
      this.innerValue = value
      if (this.type.indexOf('time') === -1) {
        this.active = false
        this.handleOutputValue(value)
      } else if (_.isNil(value)) {
        this.active = false
        this.$nextTick(() => {
          this.innerValue = null
          this.handleOutputValue(value)
        })
      } else {
        this.handleOutputValue(value)
      }
    },
    handleVisible(value) {
      this.active = !!value
    },
    handleClear() {
      if (this.type === PickerType.Date || this.type === PickerType.Time) {
        const value = this.multiple ? [] : null
        this.handleValueChange(value)
      } else if (this.type === PickerType.DateRange) {
        this.handleValueChange([])
      }
    },
    handlePicking(value) {
      this.pickingValue = value
    },
    handleStartSelected(value) {
      this.innerStart = value
    },
    handleEndSelected(value) {
      this.innerEnd = value
    },
    formatLabel(value, format) {
      if (!format) {
        if ([PickerType.Date, PickerType.DateRange].indexOf(this.type) > -1) {
          format = 'YYYY-MM-DD'
        }

        if ([PickerType.Time].indexOf(this.type) > -1) {
          switch (this.precision) {
            case 'hour':
              format = 'HH'
              break
            case 'minute':
              format = 'HH:mm'
              break
            case 'second':
            default:
              format = 'HH:mm'
          }
        }
      }

      if (_.isFunction(format)) {
        return format(value)
      } else {
        return value.format(format)
      }
    },
    renderLabel() {
      if (this.multiple) {
        return this.renderMultipleLabel()
      }
      if (this.type === PickerType.Date || this.type === PickerType.Time) {
        if (this.hasValue) {
          return (
            _.isArray(this.innerValue)
              ? _.map(this.innerValue, item => this.formatLabel(item, this.format)).join()
              : this.formatLabel(this.innerValue, this.format)
          )
        }
      } else if (this.type === PickerType.DateRange) {
        const value = this.innerValue || []
        let from = this.startPlaceholder || '开始日期'
        let to = this.endPlaceholder || '结束日期'

        from = value[0] ? this.formatLabel(value[0], this.startFormat || this.format) : from
        to = value[0] ? this.formatLabel(value[1], this.endFormat || this.format) : to

        return (
          <div
            class={{
              'km-date-picker__input-label': true,
              'km-date-picker__input-label--no-value': !value[0] || !value[1],
            }}
          >
            <div
              class={{
                'km-date-picker__input-value': true,
              }}
            >{from}</div>
            <div class="km-date-picker__input-join">{this.rangeSeparator}</div>
            <div
              class={{
                'km-date-picker__input-value': true,
              }}
            >{to}</div>
          </div>
        )
      }
    },
    renderMultipleLabel() {
      if (!this.multiple || !this.hasValue) {
        return
      }

      if (this.$slots.default) {
        return this.$slots.default
      }

      const labels = _.map(this.innerValue, (option, index) => {
        return <Tag
          key={index}
          class="km-select__label"
          size="small"
          clearable
          onClear={() => {
            this.handleLabelClear(option)
          }}
          nativeOnClick={(e) => {
            e.stopPropagation()
          }}
        >
          {this.formatLabel(option, this.format)}
        </Tag>
      })

      return (<div class="km-select-labels">
        {labels}
      </div>)
    },
    handleLabelClear(value) {
      const valueList = _.clone(this.innerValue)
      _.remove(valueList, item => item.isSame(value, 'day'))
      this.innerValue = valueList
      this.handleOutputValue(valueList)
    },
    confirm(value) {
      this.innerValue = _.clone(value)
      this.handleVisible(false)
      this.handleOutputValue(value)
    },
    cancel() {
      this.handleVisible(false)
    },
  },
  render() {
    const data = {
      scopedSlots: this.$scopedSlots,
    }

    return (
      <Popover
        placement="bottom-start"
        popperClass={this.popClass}
        value={this.active}
        onInput={this.handleVisible}
        blockPropagation={this.blockPropagation}
        disabled={this.disabled}
      >
        <PickerPane
          {...data}
          pickerType={this.type}
          value={this.innerValue}
          referenceValue={this.referenceValue ? moment(this.referenceValue) : null}
          now={this.now}
          active={this.active}
          unlinkViews={this.unlinkViews}
          shortcuts={this.shortcuts}
          disabledDate={this.disabledDate}
          onInput={this.handleValueChange}
          onPicking={this.handlePicking}
          onSartSelected={this.handleStartSelected}
          onEndSelected={this.handleEndSelected}
          precision={this.precision}
          weekStartDay={this.weekStartDay}
          multiple={this.multiple}
          paneCount={this.paneCount}
          onConfirm={this.confirm}
          onCancel={this.cancel}
        />
        <PickerInput
          pickerType={this.type}
          hidePickerIcon={this.hidePickerIcon}
          slot="reference"
          placeholder={this.placeholder}
          active={this.active}
          value={this.hasValue ? '1' : null}
          clearable={this.clearable}
          onClear={this.handleClear}
          multiple={this.multiple}
          disabled={this.disabled}
        >
          {this.$scopedSlots.input ? this.$scopedSlots.input({ value: this.value }) : this.renderLabel()}
        </PickerInput>
      </Popover>
    )
  },
}
